import React, { useEffect, useState } from 'react';
import { authHeaders } from '../../../utils/api';
import styles from './WhatsappSection.module.css';

const API = process.env.REACT_APP_API_URL || '/api';
const dateLabel = (date) => date ? new Date(date).toLocaleString('es-MX', { timeZone: 'America/Mexico_City' }) : '—';
const statusLabel = { reserved: 'Reservado', accepted: 'Aceptado por Twilio', unknown: 'Envío sin confirmar',
  cancelled: 'Cancelado', failed: 'Fallido', undelivered: 'No entregado', delivered: 'Entregado', read: 'Leído',
  sent: 'Enviado', queued: 'En cola', sending: 'Enviando' };
const levels = { bachillerato: 'Prepa', tsu: 'TSU', licenciatura: 'Licenciatura', maestria: 'Maestría', especialidad: 'Especialidad' };

export default function WhatsappRemarketingSection({ revision, dateFrom = '', dateTo = '' }) {
  const rangeKey = `${dateFrom}|${dateTo}`;
  const [filters, setFilters] = useState({ level: '', page: 1, rangeKey });
  const page = filters.rangeKey === rangeKey ? filters.page : 1;
  const level = filters.level;
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    setFilters((current) => ({ ...current, page: 1, rangeKey }));
  }, [rangeKey]);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError('');
    fetch(`${API}/admin/whatsapp-remarketing?${new URLSearchParams({ dateFrom, dateTo, level, page })}`, {
      headers: authHeaders(), signal: controller.signal, cache: 'no-store',
    }).then(async (response) => {
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || 'No se pudo consultar el remarketing.');
      if (!controller.signal.aborted) setData(body.data);
    }).catch((err) => { if (!controller.signal.aborted) { setError(err.message); setData(null); } })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [dateFrom, dateTo, level, page, revision, refresh]);
  const changeFilter = (values) => setFilters((current) => ({ ...current, ...values, page: 1 }));
  const summary = data?.summary || {};
  const funnel = [['Contactos con envío', summary.contacts || 0], ['Conversaciones reiniciadas', summary.restarted || 0], ['Registros con correo', summary.registered || 0]];
  const maxFunnel = Math.max(1, ...funnel.map(([, value]) => value));
  const maxMessages = Math.max(1, ...(data?.touches || []).map((entry) => Math.max(entry.accepted, entry.failed, entry.unknown)));
  return <section className={styles.container} aria-label="Remarketing de WhatsApp">
    <div className={styles.heading}><div><h2>Remarketing de WhatsApp</h2>
      <p>Primer toque tras 24 horas sin respuesta; segundo y tercero a los 2 y 6 días del primer toque. Envíos de 9:00 a 18:00, CDMX.</p></div>
      <button type="button" onClick={() => setRefresh((value) => value + 1)} disabled={loading}>Actualizar remarketing</button></div>
    <div className={styles.filters}>
      <label>Nivel de remarketing<select value={filters.level} onChange={(event) => changeFilter({ level: event.target.value })}>
        <option value="">Todos los niveles</option>{Object.entries(levels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
      </select></label>
    </div>
    {loading && <p role="status">Cargando remarketing…</p>}
    {error && <p role="alert">{error}</p>}
    {!loading && data && <>
      <p className={styles.note}>Incluye consultas desde {dateLabel(data.eligibleInboundSince)}, cinco días antes del despliegue de esta regla, y las nuevas consultas. Responder cancela los pendientes y habilita un nuevo ciclo si vuelve a dejar de responder; cada primer toque inicia su propio calendario. Pedir la baja o registrarse detiene este flujo.</p>
      <div className={styles.columns}>
        <div className={styles.card}><h3>Resultados de remarketing</h3>
          <div role="group" aria-label="Conversaciones y registros de remarketing" className={styles.chart}>
            {funnel.map(([label, value]) => <div key={label} className={styles.barRow} aria-label={`${label}: ${value}`}>
              <span>{label}</span><span className={styles.track}><span className={styles.activeBar} style={{ width: `${value / maxFunnel * 100}%` }} /></span><strong>{value}</strong>
            </div>)}
          </div>
          <p className={styles.note}>Contactos únicos en los envíos del período. Una respuesta posterior reinicia la conversación; un registro guardado con correo cuenta como conversión, sin exigir pago. Las conversiones se atribuyen al último ciclo enviado. No son pasos obligatoriamente consecutivos.</p>
          <div className={styles.tableScroll}><table className={styles.remarketingTable}><thead><tr><th>Nivel</th><th>Contactos</th><th>Reiniciadas</th><th>Registros</th></tr></thead>
            <tbody>{(data.levels || []).map((level) => <tr key={level.key}><th>{level.label}</th><td>{level.contacts}</td><td>{level.restarted}</td><td>{level.registered}</td></tr>)}</tbody>
          </table></div>
        </div>
        <div className={styles.card}><h3>Mensajes de remarketing por toque</h3>
          <div role="group" aria-label="Envíos de remarketing por toque" className={styles.chart}>
            {(data.touches || []).map((entry) => <React.Fragment key={entry.touch}>
              <h4>Toque {entry.touch}</h4>
              {[['Aceptados', entry.accepted], ['Entregados / leídos', entry.delivered], ['Fallidos', entry.failed], ['Sin confirmar', entry.unknown]].map(([label, value]) =>
                <div key={label} className={styles.barRow} aria-label={`Toque ${entry.touch}, ${label}: ${value}`}>
                  <span>{label}</span><span className={styles.track}><span className={label === 'Fallidos' ? styles.inactiveBar : styles.activeBar} style={{ width: `${value / maxMessages * 100}%` }} /></span><strong>{value}</strong>
                </div>)}
            </React.Fragment>)}
          </div>
          <p className={styles.note}>Entregados y fallidos usan los estados recibidos de Twilio. Un envío aceptado puede fallar después; estas cifras no se suman. Los envíos sin confirmar no se reintentan automáticamente.</p>
        </div>
      </div>
      <div className={styles.card}><h3>Historial exclusivo de remarketing ({data.total || 0})</h3>
        {!(data.messages || []).length && <p>Aún no hay mensajes de remarketing con estos filtros.</p>}
        <div className={styles.messages}>{(data.messages || []).map((message) => <article className={styles.message} key={message._id}>
          <strong>{message.name || message.target} · {message.interestLabel || levels[message.levelKey]} · Toque {message.touch}</strong>
          <small>{message.target} · {dateLabel(message.createdAt)} · {statusLabel[message.deliveryStatus || message.status] || message.status}</small>
          <p>{message.text}</p>
          {message.repliedAt && <small>Conversación reiniciada: {dateLabel(message.repliedAt)}</small>}
          {message.registeredAt && <small>Registro con correo: {dateLabel(message.registeredAt)}</small>}
          {message.error && <small>{message.error}</small>}
        </article>)}</div>
        <div className={styles.pagination}>
          <button type="button" disabled={page === 1} onClick={() => setFilters((current) => ({ ...current, page: page - 1 }))}>Remarketing anterior</button>
          <span>Página {page} de {Math.max(1, Math.ceil((data.total || 0) / (data.pageSize || 25)))}</span>
          <button type="button" disabled={page * (data.pageSize || 25) >= (data.total || 0)} onClick={() => setFilters((current) => ({ ...current, page: page + 1 }))}>Remarketing siguiente</button>
        </div>
      </div>
    </>}
  </section>;
}
